// MA-558 — kiosk-armed "class mode" chip on the kid hub (MA-DEC-0824 D2).
//
// WHY: MA-556's review found the missing signal in the founder's kiosk-trap
// walk — a device can be armed for a class (pw_kiosk_armed_class, MA-288)
// with NO visible indication anywhere on the hub surfaces a kid actually
// lands on. /play/who already shows its own "Armed for {className}" caption
// (src/routes/play.who.tsx) but that's a disjoint branch (App.jsx's
// TAB_BAR_PATHS doesn't include /play/who) — a kid who picks a profile and
// moves through /play, /journey, /read, /library sees nothing after that.
// This chip is the persistent counterpart, mounted ONCE in App.jsx's hub
// layout branch so it shows across all four hub routes without double-
// rendering with play.who's caption.
//
// Deliberately NOT interactive: no tap target, no exit affordance. The only
// way out of kiosk mode is the existing grown-up-gated door on
// KioskRosterScreen (src/screens/KioskRosterScreen.tsx, MA-545) — this chip
// is purely informational, matching the "kid-safe" framing in MA-558's item
// text (no capability a kid could misuse).
//
// `armed` is test-injectable (mirrors OfflineBanner.tsx's established
// convention for small presentational status chips): production callers
// omit it and get the real, localStorage-backed useArmedClass() signal;
// tests pass an explicit ArmedClass or null so they never need a real
// localStorage/DOM harness.
import { School } from "lucide-react";
import { useArmedClass, type ArmedClass } from "@/lib/teacher/kioskArming";

export function KioskModeChip({
  armed: armedProp,
}: {
  armed?: ArmedClass | null;
} = {}) {
  const liveArmed = useArmedClass();
  const armed = armedProp !== undefined ? armedProp : liveArmed;

  if (!armed) return null;

  return (
    <div
      data-testid="kiosk-mode-chip"
      role="status"
      aria-label={`Class mode: ${armed.className}`}
      className="pointer-events-none absolute bottom-24 right-3 z-30 flex max-w-[min(70vw,220px)] items-center gap-1.5 rounded-full border border-border bg-cream-deep px-3 py-1.5 text-xs font-bold text-muted-foreground shadow-stone"
    >
      <School size={13} aria-hidden="true" className="shrink-0" />
      <span className="truncate">Class mode · {armed.className}</span>
    </div>
  );
}
